import type { ReactElement } from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Omit } from "utility-types";
import type { IBrazilianRealEstateFundGalleryPicture } from "../../types/graphqlTypes";
import LeftChevronWithShadowIcon from "../icons/LeftChevronWithShadowIcon";
import RightChevronWithShadowIcon from "../icons/RightChevronWithShadowIcon";
import XWithShadowIcon from "../icons/XWithShadowIcon";
import * as palette from "../palette";

interface IGalleryLightboxState {
  showPicture: (picture: Omit<IBrazilianRealEstateFundGalleryPicture, "__typename">) => void;
}

interface IGalleryLightboxProps {
  pictures: Omit<IBrazilianRealEstateFundGalleryPicture, "__typename">[];
  children: (state: IGalleryLightboxState) => ReactElement;
}

export default function GalleryLightbox({ pictures, children }: IGalleryLightboxProps): ReactElement | null {
  const backdropRef = useRef(null);
  const [picture, showPicture] = useState<Omit<IBrazilianRealEstateFundGalleryPicture, "__typename"> | null>(null);

  const currentAtIndex = pictures.findIndex((candidate) => {
    return picture ? candidate.url === picture.url : false;
  });

  const nextPicture = currentAtIndex !== -1 && currentAtIndex + 1 < pictures.length && pictures[currentAtIndex + 1];
  const previousPicture = currentAtIndex !== -1 && currentAtIndex > 0 && pictures[currentAtIndex - 1];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        showPicture(null);
      }

      if (event.key === "ArrowRight" && nextPicture) {
        showPicture(nextPicture);
      }

      if (event.key === "ArrowLeft" && previousPicture) {
        showPicture(previousPicture);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextPicture, previousPicture]);

  return (
    <>
      {children({
        showPicture,
      })}

      {picture &&
        createPortal(
          <div
            css={{
              backgroundColor: "rgba(40, 40, 40, 0.5)",
              bottom: 0,
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              position: "fixed",
            }}
          >
            <div
              role="presentation"
              ref={backdropRef}
              onClick={(event) => {
                if (event.target === backdropRef.current) {
                  showPicture(null);
                }
              }}
              css={{
                width: "100%",
                height: "100%",
                display: "grid",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                css={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => showPicture(null)}
                  css={{
                    display: "inline-flex",
                    boxSizing: "border-box",
                    width: 80,
                    margin: 0,
                    padding: 0,
                    border: "none",
                    cursor: "pointer",
                    outline: "none",
                    backgroundColor: "transparent",
                  }}
                >
                  <XWithShadowIcon width={80} height={80} />
                </button>
              </div>

              <div
                css={{
                  display: "grid",
                  gridRowGap: 24,
                  textAlign: "center",
                }}
              >
                <div
                  css={{
                    position: "relative",
                  }}
                >
                  <div
                    css={{
                      width: 600,
                      height: 600,
                      boxShadow: "0 10px 50px 0 rgba(64, 64, 64, 0.4)",
                      borderRadius: 8,
                      backgroundColor: picture.dominantColor || palette.fiord,
                      userSelect: "none",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={picture.url}
                      decoding="auto"
                      alt={picture.alternativeText || "Imóvel do FII"}
                      css={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>

                  {previousPicture && (
                    <div
                      css={{
                        position: "absolute",
                        transform: "translateY(-50%)",
                        top: "50%",
                        left: -88,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => showPicture(previousPicture)}
                        css={{
                          padding: 0,
                          width: 88,
                          display: "inline-flex",
                          justifyContent: "center",
                          alignItems: "center",
                          margin: 0,
                          border: "none",
                          cursor: "pointer",
                          outline: "none",
                          backgroundColor: "transparent",
                          position: "relative",
                        }}
                      >
                        <LeftChevronWithShadowIcon width={40} height={74} />
                      </button>
                    </div>
                  )}

                  {nextPicture && (
                    <div
                      css={{
                        position: "absolute",
                        transform: "translateY(-50%)",
                        top: "50%",
                        right: -88,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => showPicture(nextPicture)}
                        css={{
                          padding: 0,
                          width: 88,
                          display: "inline-flex",
                          justifyContent: "center",
                          alignItems: "center",
                          margin: 0,
                          border: "none",
                          cursor: "pointer",
                          outline: "none",
                          backgroundColor: "transparent",
                          position: "relative",
                        }}
                      >
                        <RightChevronWithShadowIcon width={40} height={74} />
                      </button>
                    </div>
                  )}
                </div>

                {picture.alternativeText && (
                  <div
                    css={{
                      textAlign: "center",
                    }}
                  >
                    <div
                      css={{
                        display: "inline-flex",
                        borderRadius: 12,
                        backgroundColor: palette.tundora,
                        boxShadow: "0 10px 20px 0 rgba(0, 0, 0, 0.5)",
                        padding: "16px 24px",
                        maxWidth: picture.width,
                      }}
                    >
                      <h1
                        css={{
                          margin: 0,
                          lineHeight: 1.33,
                          fontSize: 21,
                          fontWeight: "bold",
                          letterSpacing: 0.9,
                          color: palette.white,
                          display: "inline-flex",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          userSelect: "none",
                        }}
                      >
                        {picture.alternativeText}
                      </h1>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
